import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { SettingsProvider } from './contexts/SettingsContext.tsx';

// Pages
import Login from './pages/Login.tsx';
import LoginMfa from './pages/LoginMfa.tsx';
import SecuritySettings from './pages/settings/SecuritySettings.tsx';
import MfaSetup from './pages/settings/MfaSetup.tsx';
import MfaBackupCodes from './pages/settings/MfaBackupCodes.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Assets from './pages/Assets.tsx';
import CreateAsset from './pages/CreateAsset.tsx';
import AssetDetailNew from './pages/AssetDetailNew.tsx';
import AssetExchange from './pages/AssetExchange.tsx';
import AssetSale from './pages/AssetSale.tsx';
import EditAsset from './pages/EditAsset.tsx';
import Vehicles from './pages/Vehicles.tsx';
import Vessels from './pages/Vessels.tsx';
import CreateVehicle from './pages/CreateVehicle.tsx';
import CreateVessel from './pages/CreateVessel.tsx';
import VehicleDetail from './pages/VehicleDetail.tsx';
import VesselDetail from './pages/VesselDetail.tsx';
import VehicleSale from './pages/VehicleSale.tsx';
import VesselSale from './pages/VesselSale.tsx';
import EditVehicle from './pages/EditVehicle.tsx';
import EditVessel from './pages/EditVessel.tsx';
import Aircrafts from './pages/Aircrafts.tsx';
import CreateAircraft from './pages/CreateAircraft.tsx';
import AircraftDetail from './pages/AircraftDetail.tsx';
import EditAircraft from './pages/EditAircraft.tsx';
import AircraftSale from './pages/AircraftSale.tsx';
import NewReports from './pages/NewReports.tsx';
import Debts from './pages/Debts.tsx';
import CreateDebt from './pages/CreateDebt.tsx';
import CreatePayable from './pages/financeiro/CreatePayable.tsx';
import DebtDetail from './pages/DebtDetail.tsx';
import EditDebt from './pages/EditDebt.tsx';
import Receivables from './pages/Receivables.tsx';
import CreateReceivable from './pages/CreateReceivable.tsx';
import ReceivableDetail from './pages/ReceivableDetail.tsx';
import EditReceivable from './pages/EditReceivable.tsx';
import Rentals from './pages/Rentals.tsx';
import CreateRental from './pages/CreateRental.tsx';
import CreateRentalGlobal from './pages/CreateRentalGlobal.tsx';
import EditRental from './pages/EditRental.tsx';
import UserManagement from './pages/UserManagement.tsx';
import EditUser from './pages/EditUser.tsx';
import UserDetail from './pages/UserDetail.tsx';
import CreateUser from './pages/CreateUser.tsx';
import Whitelabel from './pages/Whitelabel.tsx';
import Companies from './pages/Companies.tsx';
import EditCompany from './pages/EditCompany.tsx';
import CompanyDetail from './pages/CompanyDetail.tsx';
import Investments from './pages/Investments.tsx';
import CreateInvestment from './pages/CreateInvestment.tsx';
import ImportInvestments from './pages/ImportInvestments.tsx';
import InvestmentDetail from './pages/InvestmentDetail.tsx';
import EditInvestment from './pages/EditInvestment.tsx';
import UpdateInvestmentPrice from './pages/UpdateInvestmentPrice.tsx';
import Philanthropies from './pages/Philanthropies.tsx';
import CreatePhilanthropy from './pages/CreatePhilanthropy.tsx';
import EditPhilanthropy from './pages/EditPhilanthropy.tsx';
import PhilanthropyDetail from './pages/PhilanthropyDetail.tsx';
import ExchangeRegister from './pages/ExchangeRegister.tsx';
import FluxoDeCaixa from './pages/financeiro/FluxoDeCaixa.tsx';
import NovoLancamento from './pages/financeiro/NovoLancamento.tsx';
import Contas from './pages/financeiro/Contas.tsx';
import ContasAPagar from './pages/financeiro/ContasAPagar.tsx';
import ContasAReceber from './pages/financeiro/ContasAReceber.tsx';
import DividasFinanceiro from './pages/financeiro/Dividas.tsx';
import SettlementPage from './pages/financeiro/SettlementPage.tsx';
import LancamentosAutomaticos from './pages/financeiro/LancamentosAutomaticos.tsx';
import ExecutarLancamentosAuto from './pages/financeiro/ExecutarLancamentosAuto.tsx';
import ConciliacaoOfx from './pages/financeiro/ConciliacaoOfx.tsx';
import ConciliarOfx from './pages/financeiro/ConciliarOfx.tsx';
import DesconciliarOfx from './pages/financeiro/DesconciliarOfx.tsx';
import FrotaLista from './pages/frota/FrotaLista.tsx';
import FrotaNovo from './pages/frota/FrotaNovo.tsx';
import FrotaDetalhe from './pages/frota/FrotaDetalhe.tsx';
import FrotaEditar from './pages/frota/FrotaEditar.tsx';
import FrotaRegistrarKm from './pages/frota/FrotaRegistrarKm.tsx';
import FrotaManutencoes from './pages/frota/FrotaManutencoes.tsx';
import FrotaNovaManutencao from './pages/frota/FrotaNovaManutencao.tsx';
import FrotaCustos from './pages/frota/FrotaCustos.tsx';
import FrotaNovoCusto from './pages/frota/FrotaNovoCusto.tsx';
import FrotaDocumentos from './pages/frota/FrotaDocumentos.tsx';
import FrotaDocumentoForm from './pages/frota/FrotaDocumentoForm.tsx';

// Componentes
import ProtectedRoute from './components/ProtectedRoute.tsx';
import Layout from './components/Layout.tsx';

// Criar instância do QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SettingsProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/login/mfa" element={<LoginMfa />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute requiredResource="dashboard">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredResource="dashboard">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Assets />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateAsset />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/assets/create" element={<Navigate to="/assets/novo" replace />} />

              <Route
                path="/assets/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <AssetDetailNew />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/exchange"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <AssetExchange />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/exchanges/new"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <ExchangeRegister />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditAsset />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assets/:id/edit"
                element={<Navigate to="../editar" relative="path" replace />}
              />

              <Route
                path="/assets/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <AssetSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Rentals */}
              <Route
                path="/rentals"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Rentals />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rentals/new"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <CreateRentalGlobal />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rentals/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <EditRental />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rentals/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditRental />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assets/:id/rentals/new"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <CreateRental />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <ProtectedRoute requiredPermission="users.read">
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/new"
                element={
                  <ProtectedRoute requiredPermission="users.create">
                    <Layout>
                      <CreateUser />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Vehicles />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Vessels */}
              <Route
                path="/vessels"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Vessels />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditVehicle />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-vehicle/:id"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditVehicle />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vessels/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditVessel />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-vessel/:id"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditVessel />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateVehicle />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/vehicles/new" element={<Navigate to="/vehicles/novo" replace />} />

              <Route
                path="/vessels/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateVessel />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/vessels/new" element={<Navigate to="/vessels/novo" replace />} />

              <Route
                path="/vehicles/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <VehicleDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vessels/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <VesselDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vehicles/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <VehicleSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vessels/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <VesselSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Aircrafts */}
              <Route
                path="/aircrafts"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <Aircrafts />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircrafts/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <CreateAircraft />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/aircrafts/new" element={<Navigate to="/aircrafts/novo" replace />} />

              <Route
                path="/aircrafts/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <AircraftDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircrafts/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditAircraft />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-aircraft/:id"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <EditAircraft />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/aircrafts/:id/sale"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <AircraftSale />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Companies */}
              <Route
                path="/companies"
                element={
                  <ProtectedRoute requiredResource="companies">
                    <Layout>
                      <Companies />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/companies/novo"
                element={
                  <ProtectedRoute requiredPermission="companies.create">
                    <Layout>
                      <EditCompany />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/companies/new" element={<Navigate to="/companies/novo" replace />} />

              <Route
                path="/companies/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="companies.update">
                    <Layout>
                      <EditCompany />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/companies/:id/edit"
                element={<Navigate to="../editar" relative="path" replace />}
              />

              <Route
                path="/companies/:id"
                element={
                  <ProtectedRoute requiredResource="companies">
                    <Layout>
                      <CompanyDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Investments */}
              <Route
                path="/investments"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <Investments />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/novo"
                element={
                  <ProtectedRoute requiredPermission="investments.create">
                    <Layout>
                      <CreateInvestment />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments/new"
                element={<Navigate to="/investments/novo" replace />}
              />
              <Route
                path="/investments/create"
                element={<Navigate to="/investments/novo" replace />}
              />

              <Route
                path="/investments/importar"
                element={
                  <ProtectedRoute requiredPermission="investments.create">
                    <Layout>
                      <ImportInvestments />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/:id"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <InvestmentDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/:id/atualizar-preco"
                element={
                  <ProtectedRoute requiredPermission="investments.update">
                    <Layout>
                      <UpdateInvestmentPrice />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/investments/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="investments.update">
                    <Layout>
                      <EditInvestment />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/investments/:id/edit"
                element={<Navigate to="../editar" relative="path" replace />}
              />

              {/* Philanthropies — static paths before :id */}
              <Route
                path="/philanthropies"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <Philanthropies />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/philanthropies/novo"
                element={
                  <ProtectedRoute requiredPermission="investments.create">
                    <Layout>
                      <CreatePhilanthropy />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/philanthropies/create" element={<Navigate to="/philanthropies/novo" replace />} />
              <Route
                path="/philanthropies/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="investments.update">
                    <Layout>
                      <EditPhilanthropy />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/philanthropies/:id/edit"
                element={<Navigate to="../editar" relative="path" replace />}
              />
              <Route
                path="/philanthropies/:id"
                element={
                  <ProtectedRoute requiredResource="investments">
                    <Layout>
                      <PhilanthropyDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Frota — Plano 2 (páginas dedicadas, sem modais) */}
              <Route
                path="/patrimonio/frota"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <FrotaLista />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <FrotaNovo />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <FrotaDetalhe />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <FrotaEditar />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/registrar-km"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <FrotaRegistrarKm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/documentos"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <FrotaDocumentos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/documentos/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <FrotaDocumentoForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/documentos/:docId/editar"
                element={
                  <ProtectedRoute requiredPermission="assets.update">
                    <Layout>
                      <FrotaDocumentoForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/manutencoes"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <FrotaManutencoes />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/manutencoes/nova"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <FrotaNovaManutencao />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/custos"
                element={
                  <ProtectedRoute requiredResource="assets">
                    <Layout>
                      <FrotaCustos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patrimonio/frota/:id/custos/novo"
                element={
                  <ProtectedRoute requiredPermission="assets.create">
                    <Layout>
                      <FrotaNovoCusto />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Financeiro — Plano 1.1+A */}
              <Route
                path="/financeiro/fluxo-de-caixa"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <FluxoDeCaixa />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/fluxo-de-caixa/novo"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <NovoLancamento />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <Contas />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas-a-pagar"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <ContasAPagar />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas-a-pagar/nova"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <CreatePayable />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas/pagar/nova"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <CreatePayable />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas/pagar/:id/pagar"
                element={
                  <ProtectedRoute requiredPermission="debts.update">
                    <Layout>
                      <SettlementPage kind="payable" />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas/receber/nova"
                element={
                  <ProtectedRoute requiredPermission="receivables.create">
                    <Layout>
                      <CreateReceivable />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas/receber/:id/receber"
                element={
                  <ProtectedRoute requiredPermission="receivables.update">
                    <Layout>
                      <SettlementPage kind="receivable" />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/dividas/:id/pagar"
                element={
                  <ProtectedRoute requiredPermission="debts.update">
                    <Layout>
                      <SettlementPage kind="debt" />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas-a-pagar/:id/editar"
                element={
                  <ProtectedRoute requiredPermission="debts.update">
                    <Layout>
                      <EditDebt />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas-a-pagar/:id"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <DebtDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas-a-receber"
                element={
                  <ProtectedRoute requiredResource="receivables">
                    <Layout>
                      <ContasAReceber />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/contas-a-receber/nova"
                element={
                  <ProtectedRoute requiredPermission="receivables.create">
                    <Layout>
                      <CreateReceivable />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/dividas"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <DividasFinanceiro />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/dividas/nova"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <CreateDebt />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/financeiro/lancamentos-automaticos"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <LancamentosAutomaticos />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/lancamentos-automaticos/executar"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <ExecutarLancamentosAuto />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/conciliacao-ofx"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <ConciliacaoOfx />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/conciliacao-ofx/:txId/conciliar"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <ConciliarOfx />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/conciliacao-ofx/:txId/desconciliar"
                element={
                  <ProtectedRoute requiredPermission="debts.create">
                    <Layout>
                      <DesconciliarOfx />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Legacy redirects */}
              <Route path="/debts" element={<Navigate to="/financeiro/dividas" replace />} />
              <Route
                path="/receivables"
                element={<Navigate to="/financeiro/contas?tab=receber" replace />}
              />

              <Route
                path="/receivables/new"
                element={<Navigate to="/financeiro/contas/receber/nova" replace />}
              />

              <Route
                path="/receivables/:id"
                element={
                  <ProtectedRoute requiredResource="receivables">
                    <Layout>
                      <ReceivableDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/receivables/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="receivables.update">
                    <Layout>
                      <EditReceivable />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/debts/new"
                element={<Navigate to="/financeiro/dividas/nova" replace />}
              />

              <Route
                path="/debts/:id"
                element={
                  <ProtectedRoute requiredResource="debts">
                    <Layout>
                      <DebtDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/debts/:id/edit"
                element={
                  <ProtectedRoute requiredPermission="debts.update">
                    <Layout>
                      <EditDebt />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route path="/reports" element={<Navigate to="/new-reports" replace />} />

              <Route
                path="/new-reports"
                element={
                  <ProtectedRoute requiredResource="reports">
                    <Layout>
                      <NewReports />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/edit/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <EditUser />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/:id"
                element={
                  <ProtectedRoute requiredResource="users" requiredPermission="users.read">
                    <Layout>
                      <UserDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Navigate to="/users/edit/current" replace />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/security"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SecuritySettings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/security/mfa/setup"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MfaSetup />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/security/mfa/backup"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MfaBackupCodes />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/whitelabel"
                element={
                  <ProtectedRoute
                    requiredResource="whitelabel"
                    requiredPermission="whitelabel.update"
                  >
                    <Layout>
                      <Whitelabel />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Dead aliases after Plano 3/4 batch */}
              <Route
                path="/preview"
                element={<Navigate to="/financeiro/lancamentos-automaticos/executar" replace />}
              />
              <Route
                path="/historico"
                element={
                  <Navigate to="/financeiro/lancamentos-automaticos?tab=historico" replace />
                }
              />
              <Route
                path="/importar"
                element={<Navigate to="/financeiro/conciliacao-ofx" replace />}
              />

              {/* Redirecionar para dashboard se a rota não existir */}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Router>
        </SettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
